// Life stages and the brooder thermal model (GENETICS.md §1).

import type { ChickenEntity, LifeStage } from '../core/types';
import { breedProfile, LIFE_STAGES, type BiologyConfig } from './config';

/** chick 0–6wk, pullet 7–19wk, adult 20wk+ — the real feed-stage boundaries. */
export function stageForAge(ageInDays: number): LifeStage {
  if (ageInDays <= LIFE_STAGES.chickMaxDays) return 'chick';
  if (ageInDays <= LIFE_STAGES.pulletMaxDays) return 'pullet';
  return 'adult';
}

export function feedTypeForStage(stage: LifeStage): 'starter' | 'grower' | 'layer' {
  if (stage === 'chick' || stage === 'egg') return 'starter';
  return stage === 'pullet' ? 'grower' : 'layer';
}

/** True once the hen is old enough to lay for her breed (Silkies start later). */
export function isLayingAge(chicken: ChickenEntity): boolean {
  if (chicken.gender !== 'hen' || chicken.stage !== 'adult') return false;
  return chicken.ageInDays >= breedProfile(chicken.breed).onsetWeeks * 7;
}

/** Target brooder floor temperature: T(w) = 95 − 5w (GENETICS.md §1.1). */
export function brooderTargetTempF(ageInWeeks: number, config: BiologyConfig): number {
  const b = config.brooder;
  const w = Math.min(Math.max(ageInWeeks, 0), b.weeksUntilAmbient);
  return b.startTempF - b.dropPerWeekF * w;
}

/** Thermal Stress Index: |ambient − target| (GENETICS.md §1.2). */
export function thermalStressIndex(
  ageInDays: number,
  ambientF: number,
  config: BiologyConfig,
): number {
  return Math.abs(ambientF - brooderTargetTempF(ageInDays / 7, config));
}

export type BrooderCue = 'optimal' | 'huddling' | 'panting';

/**
 * The behaviour the chicks show, which is how the player is meant to read the
 * temperature — huddled under the lamp is cold, pressed to the corners is hot.
 */
export function brooderCue(
  ageInDays: number,
  ambientF: number,
  config: BiologyConfig,
): BrooderCue {
  const target = brooderTargetTempF(ageInDays / 7, config);
  if (Math.abs(ambientF - target) <= config.brooder.optimalTsiF) return 'optimal';
  return ambientF < target ? 'huddling' : 'panting';
}

/** Health drain from sustained brooder error: ΔH = −0.5 × TSI per hour. */
export function brooderHealthLossPerHour(tsi: number, config: BiologyConfig): number {
  return tsi > config.brooder.stressTsiF ? config.brooder.healthLossPerTsiPerHour * tsi : 0;
}

/** Molt: annual, ~18 months on, laying stops, protein demand rises. */
export function shouldStartMolt(chicken: ChickenEntity, config: BiologyConfig): boolean {
  if (chicken.gender !== 'hen' || chicken.moltingUntilDay !== undefined) return false;
  return chicken.ageInDays >= config.molt.firstMoltDays;
}

export function isMolting(chicken: ChickenEntity, currentDay: number): boolean {
  return chicken.moltingUntilDay !== undefined && currentDay < chicken.moltingUntilDay;
}
